import { ILoginResponse } from '@common/types/user';
import { SplitLayout } from '@components/layout';
import { AuthenticationHeader } from '@components/layout/authentication/AuthenticationHeader';
import { useAuth } from '@hooks/useAuth';
import { usePrefixedNavigate } from '@hooks/usePrefixedNavigate';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Button, CircularProgress, Typography } from '@mui/material';
import { useGetTwoFARolesQuery, useLazyGetKeycloakLoginUrlQuery } from '@services/keycloak';
import { pathToVP } from '@utils/route';
import { isAfter } from 'date-fns';
import { useCallback, useEffect, useMemo } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

const LinkAccount = () => {
    const { locale } = useIntl();
    const { user, migrationDate } = useAuth({ disableUserMigrationVerification: true });
    const migrationDeadlinePassed = useMemo(() => isAfter(new Date(), migrationDate), [migrationDate]);
    const { data: twoFARoles } = useGetTwoFARolesQuery();
    const navigate = usePrefixedNavigate();
    const { state } = useLocation();
    const userDetails = (state as { userDetails?: ILoginResponse & { password: string } } | undefined)?.userDetails;
    const [searchParams] = useSearchParams();
    const nextParam = searchParams.get('next');

    useEffect(() => {
        if (!userDetails) {
            const newParams = new URLSearchParams(searchParams);
            navigate({ pathname: '/auth/login', search: newParams.toString() }, { replace: true });
            return;
        }

        window.history.replaceState(
            { ...state, userDetails: undefined },
            document.title,
            window.location.pathname + window.location.search
        );
    }, []);

    const userRoleRequires2FA = useMemo(
        () => user && twoFARoles?.some((role) => role.name === user.role),
        [user, twoFARoles]
    );

    const [getKeycloakLoginUrl, { isLoading: isLoadingLoginUrl }] = useLazyGetKeycloakLoginUrlQuery();

    const handleAccountConnection = useCallback(() => {
        if (!userDetails) return;
        getKeycloakLoginUrl({ email: userDetails.email, prompt_login: true, locale })
            .unwrap()
            .then(({ login_url }) => {
                window.location.href = login_url;
            });
        return;
    }, [userDetails]);

    const isVictronEmail = userDetails?.email.endsWith('@victronenergy.com');

    const backToProfUrl = useMemo(() => {
        const newParams = new URLSearchParams(searchParams);

        if (!nextParam || (nextParam && !nextParam.startsWith('/app'))) {
            return pathToVP(
                '',
                Array.from(searchParams.entries()).map(([name, value]) => ({ name, value }))
            ).toString();
        } else {
            newParams.delete('next');
            return `${nextParam}?${newParams.toString()}`;
        }
    }, [searchParams]);

    return (
        <>
            <title>Victron Professional - Account Linking</title>
            <SplitLayout>
                <AuthenticationHeader />
                <Alert severity="info" icon={<InfoOutlinedIcon color="info-main" />}>
                    <Typography fontFamily="Roboto" fontWeight={500} fontSize={16}>
                        <FormattedMessage
                            id="auth.linkAccount.title"
                            defaultMessage="{brandName} Account Detected"
                            description="Title when OneID account is detected for linking. Used in: /auth/link-account"
                            values={{ brandName: 'Victron OneID' }}
                        />
                    </Typography>
                    <Typography fontFamily="Roboto" fontWeight={400} fontSize={14} mt={1}>
                        <FormattedMessage
                            id="auth.linkAccount.accountExists"
                            defaultMessage="We noticed you already have an account with the same email in {brandName}."
                            description="Message explaining existing account was found. Used in: /auth/link-account"
                            values={{
                                brandName: (
                                    <Typography component="span" fontWeight={500} fontFamily="Roboto" fontSize={14}>
                                        Victron OneID
                                    </Typography>
                                ),
                            }}
                        />
                    </Typography>
                    <Typography fontFamily="Roboto" fontWeight={400} fontSize={14}>
                        <FormattedMessage
                            id="auth.linkAccount.confirmDescription"
                            defaultMessage="After clicking on 'Confirm', you will be able to login into your {professionalBrand} account using your {oneIdBrand}."
                            description="Explanation of what happens after confirming account link. Used in: /auth/link-account"
                            values={{
                                professionalBrand: 'Victron Professional',
                                oneIdBrand: (
                                    <Typography component="span" fontWeight={500} fontFamily="Roboto" fontSize={14}>
                                        Victron OneID
                                    </Typography>
                                ),
                            }}
                        />
                    </Typography>
                    {isVictronEmail ? (
                        <Typography fontFamily="Roboto" fontWeight={400} fontSize={14}>
                            <FormattedMessage
                                id="auth.linkAccount.victronEmailRedirect"
                                defaultMessage="Since you are using a {domain} address, you will be redirected to Microsoft to sign in."
                                description="Notice for Victron employees about Microsoft redirect. Used in: /auth/link-account"
                                values={{ domain: '@victronenergy.com' }}
                            />
                        </Typography>
                    ) : null}
                    {userRoleRequires2FA ? (
                        <Typography fontFamily="Roboto" fontWeight={400} fontSize={14} mt={2}>
                            <FormattedMessage
                                id="auth.common.twoFactorRequired"
                                defaultMessage="Due to your role 2FA setup will be mandatory."
                                description="Notice that 2FA is required for certain user roles. Used in: /auth/migrate-account and /auth/link-account"
                            />
                        </Typography>
                    ) : null}
                    {!isNaN(migrationDate.getTime()) ? (
                        <Typography fontFamily="Roboto" fontWeight={600} fontSize={14} mt={2} color={'error'}>
                            {migrationDeadlinePassed ? (
                                <FormattedMessage
                                    id="auth.linkAccount.migrationDeadlinePassed"
                                    defaultMessage="The migration deadline for your account has passed. To continue, please migrate your account."
                                    description="Warning when migration deadline has expired. Used in: /auth/link-account and /auth/migrate-account"
                                />
                            ) : (
                                <FormattedMessage
                                    id="auth.linkAccount.migrationDeadline"
                                    defaultMessage="Your account must be migrated by {date}."
                                    description="Notice showing migration deadline date. Used in: /auth/migrate-account"
                                    values={{
                                        date: (
                                            <FormattedDate
                                                value={migrationDate}
                                                year="numeric"
                                                month="long"
                                                day="numeric"
                                            />
                                        ),
                                    }}
                                />
                            )}
                        </Typography>
                    ) : null}
                </Alert>
                <Button
                    className="rounded-full black-border mt-6"
                    fullWidth
                    onClick={handleAccountConnection}
                    startIcon={isLoadingLoginUrl ? <CircularProgress size={20} /> : null}
                    disabled={isLoadingLoginUrl}
                >
                    <span>
                        <FormattedMessage
                            id="auth.linkAccount.confirmButton"
                            defaultMessage="Confirm Account Migration"
                            description="Text for confirm migration button. Used in: /auth/link-account and /auth/migrate-account"
                        />
                    </span>
                </Button>
                {!migrationDeadlinePassed ? (
                    <Button
                        disabled={isLoadingLoginUrl}
                        color="white"
                        className="rounded-full black-border mt-2"
                        fullWidth
                        {...(nextParam?.startsWith('/app')
                            ? { component: Link, to: backToProfUrl }
                            : { href: backToProfUrl })}
                    >
                        <FormattedMessage
                            id="auth.linkAccount.skipButton"
                            defaultMessage="Skip Account Migration"
                            description="Text for skip migration button. Used in: /auth/link-account and /auth/migrate-account"
                        />
                    </Button>
                ) : null}
            </SplitLayout>
        </>
    );
};

export { LinkAccount };
